import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Mail, Phone, Clock, Calendar, User, MapPin, DollarSign, Hash, CreditCard, Building, X, MessageSquare, AlertTriangle } from 'lucide-react';
import { validateCanadianPhoneNumber, formatPhoneNumber } from '@/utils/phoneUtils';
import { cn } from '@/lib/utils';

interface FormFieldProps {
  id: string;
  name: string;
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoComplete?: string;
  className?: string;
  required?: boolean;
  maxLength?: number;
  showClear?: boolean;
  onClear?: () => void;
  error?: boolean;
}

export const FormField: React.FC<FormFieldProps> = ({
  id,
  name,
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  autoComplete,
  className = 'h-7 text-sm',
  required = false,
  maxLength,
  showClear = false,
  onClear,
  error = false,
}) => {
  const [phoneError, setPhoneError] = useState<string>('');
  const isPhoneField = name.toLowerCase().includes('phone') || name.toLowerCase().includes('tel') || type === 'tel';

  // Validate phone number for Canadian area codes
  useEffect(() => {
    if (isPhoneField && value && value.length >= 3) {
      const validation = validateCanadianPhoneNumber(value);
      if (!validation.isValid && validation.error) {
        setPhoneError(validation.error);
      } else {
        setPhoneError('');
      }
    } else {
      setPhoneError('');
    }
  }, [value, isPhoneField]);

  const handlePhoneChange = (newValue: string) => {
    if (isPhoneField) {
      // Format the phone number as user types
      const formatted = formatPhoneNumber(newValue);
      onChange(formatted);
    } else {
      onChange(newValue);
    }
  };
  // Get appropriate icon based on field type and name
  const getFieldIcon = () => {
    const fieldName = name.toLowerCase();
    const fieldType = type.toLowerCase();
    
    if (fieldName.includes('email') || fieldType === 'email') {
      return <Mail className="h-4 w-4 text-gray-400" />;
    }
    if (fieldName.includes('phone') || fieldName.includes('tel') || fieldType === 'tel') {
      return <Phone className="h-4 w-4 text-gray-400" />;
    }
    if (fieldName.includes('time') || fieldName.includes('hour')) {
      return <Clock className="h-4 w-4 text-gray-400" />;
    }
    if (fieldName.includes('date') || fieldType === 'date') {
      return <Calendar className="h-4 w-4 text-gray-400" />;
    }
    if (fieldName.includes('name') || fieldName.includes('first') || fieldName.includes('last')) {
      return <User className="h-4 w-4 text-gray-400" />;
    }
    if (fieldName.includes('address') || fieldName.includes('city') || fieldName.includes('zip')) {
      return <MapPin className="h-4 w-4 text-gray-400" />;
    }
    if (fieldName.includes('price') || fieldName.includes('amount') || fieldName.includes('salary') || fieldName.includes('income')) {
      return <DollarSign className="h-4 w-4 text-gray-400" />;
    }
    if (fieldName.includes('vin') || fieldName.includes('stock') || fieldName.includes('id')) {
      return <Hash className="h-4 w-4 text-gray-400" />;
    }
    if (fieldName.includes('card') || fieldName.includes('credit')) {
      return <CreditCard className="h-4 w-4 text-gray-400" />;
    }
    if (fieldName.includes('company') || fieldName.includes('employer') || fieldName.includes('business')) {
      return <Building className="h-4 w-4 text-gray-400" />;
    }
    if (fieldName.includes('question') || fieldName.includes('message') || fieldType === 'textarea') {
      return <MessageSquare className="h-4 w-4 text-gray-400" />;
    }
    return null;
  };

  const icon = getFieldIcon();
  const isTextarea = type === 'textarea';

  return (
    <div className="space-y-1">
      <Label htmlFor={id} className={cn("text-xs", error ? "text-red-500" : "text-gray-700")}>
        {label}
      </Label>
      <div className="relative">
        {icon && !isTextarea && (
          <div className="absolute left-1 top-1/2 transform -translate-y-1/2 pointer-events-none z-10">
            {icon}
          </div>
        )}
        {icon && isTextarea && (
          <div className="absolute left-1 top-3 pointer-events-none z-10">
            {icon}
          </div>
        )}
        {showClear && value && onClear && (
          <button
            type="button"
            onClick={onClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
            title="Clear field"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {isTextarea ? (
          <Textarea
            id={id}
            name={name}
            value={value}
            onChange={(e) => handlePhoneChange(e.target.value)}
            placeholder={placeholder}
            className={`min-h-[40px] resize-y ${icon ? 'pl-10' : ''} ${showClear && value ? 'pr-10' : ''} ${phoneError ? '!border-red-500 focus:!border-red-500 !bg-red-50 !border-2' : ''}`}
            maxLength={maxLength}
          />
        ) : (
          <Input
            id={id}
            name={name}
            type={type}
            autoComplete={autoComplete}
            value={value}
            onChange={(e) => handlePhoneChange(e.target.value)}
            placeholder={placeholder}
            className={`${className} ${icon ? 'pl-10' : ''} ${showClear && value ? 'pr-10' : ''} ${phoneError ? '!border-red-500 focus:!border-red-500 !bg-red-50 !border-2' : ''}`}
            required={required}
            maxLength={maxLength}
          />
        )}
        {phoneError && isPhoneField && (
          <div className="flex items-center gap-1 mt-1">
            <AlertTriangle className="h-3 w-3 text-red-500" />
            <span className="text-xs text-red-600">{phoneError}</span>
          </div>
        )}
      </div>
    </div>
  );
};